import { useNavigate } from "react-router-dom";
import BreadCumb from "../../components/common/Breadcrumb";
import backArrow from "../../assets/task/back_arrow.svg";
import { useEffect, useMemo, useState } from "react";
import LeaveCalendar from "../../components/leave/LeaveCalendar";
import SelectedDates from "../../components/leave/SelectedDates";
import LeaveDetails from "../../components/leave/LeaveDetails";
import StatsPanel from "../../components/leave/StatsPanel";
import { useLazyQuery } from "@apollo/client";
import { LEAVES_BY_EMPLOYEE,EMPLOYEE_BY_EMPLOYEE_ID } from "../../api/query";
import Loader from "../../components/common/Loader";
import { useAuth } from "../../context/AuthContext";
import { endOfYear, format, parse, startOfYear } from "date-fns";
import DynamicTable from "../../components/common/DynamicTable";
import { SortingState } from "@tanstack/react-table";
import { getAvailableBalance } from "./leaveCalculator";

const columns: any[] = [
    {
        key: "start_date",
        name: "Start Date",
        id: "start_date",
        header: "Start Date",
        accessorKey: "start_date",
        enableSorting: true,
        sortingFn: (rowA: any, rowB: any) => {
            const startDateA = rowA?.getValue("start_date");
            const startDateB = rowB?.getValue("start_date");

            // If a start_date is missing (null or undefined), treat it as the latest date.
            // You can also treat missing dates as the earliest date by using `new Date(0)` instead of `new Date()`
            const defaultDate = new Date(0); // You can change this to `new Date()` if you want it sorted to the end.

            const dateA = startDateA ? parse(startDateA, "MM-dd-yyyy", new Date()) : defaultDate;
            const dateB = startDateB ? parse(startDateB, "MM-dd-yyyy", new Date()) : defaultDate;

            // Ensure valid date parsing, otherwise fallback to defaultDate
            if (isNaN(dateA.getTime())) {
                dateA.setTime(defaultDate.getTime());
            }
            if (isNaN(dateB.getTime())) {
                dateB.setTime(defaultDate.getTime());
            }

            // Compare the full dates directly (ascending or descending will be handled by react-table)
            return dateA.getTime() - dateB.getTime();
        },
    },
    {
        key: "leave_type",
        name: "Leae Type",
        id: "leave_type",
        header: "Leave Type",
        accessorKey: "leave_type",
        enableSorting: true,
        sortingFn: (rowA: any, rowB: any, columnId: any) => {
            const valueA = String(
                rowA.getValue(columnId) || ""
            ).toLowerCase();
            const valueB = String(
                rowB.getValue(columnId) || ""
            ).toLowerCase();
            return valueA.localeCompare(valueB);
        },
    },
    {
        key: "reason",
        name: "Reason",
        id: "reason",
        header: "Reason",
        accessorKey: "reason",
        enableSorting: true,
        sortingFn: (rowA: any, rowB: any, columnId: any) => {
            const valueA = String(
                rowA.getValue(columnId) || ""
            ).toLowerCase();
            const valueB = String(
                rowB.getValue(columnId) || ""
            ).toLowerCase();
            return valueA.localeCompare(valueB);
        },
    },
    {
    key: "is_half_day",
    name: "Leave Duration",
    id: "is_half_day",
    header: "Leave Duration",
    accessorKey: "is_half_day",
    enableSorting: true,
    sortingFn: (rowA: any, rowB: any, columnId: any) =>
        String(rowA.getValue(columnId) || "").localeCompare(
            String(rowB.getValue(columnId) || "")
        ),
},
    {
        key: "status",
        name: "Status",
        id: "status",
        header: "Status",
        accessorKey: "status",
        enableSorting: true,
        sortingFn: (rowA: any, rowB: any, columnId: any) => {
            const valueA = String(
                rowA.getValue(columnId) || ""
            ).toLowerCase();
            const valueB = String(
                rowB.getValue(columnId) || ""
            ).toLowerCase();
            return valueA.localeCompare(valueB);
        },
    },
    {
    key: "pay_type",
    name: "Pay Type",
    id: "pay_type",
    header: "Pay Type",
    accessorKey: "pay_type",
    enableSorting: true,
    sortingFn: (rowA: any, rowB: any, columnId: any) =>
        String(rowA.getValue(columnId) || "").localeCompare(String(rowB.getValue(columnId) || "")),
},
]

export interface SelectedDate {
    date: Date;
    isHalfDay: boolean;
}

const ApplyLeave = () => {
    const navigate = useNavigate();

    const { userInfo } = useAuth()
    const employeeID = userInfo?.employee_id || '';

    const [selectedDates, setSelectedDates] = useState<SelectedDate[]>([]);
    const [showLeaves, setShowLeaves] = useState(false);
    const [sorting, setSorting] = useState<SortingState>([]);
    const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
    const [mainTable, setMainTable] = useState<any>(null);
    const [mainTableUpdate, setMainTableUpdate] = useState(false);


    const [
    getEmployeeById,
    { data: employeeData, loading: employeeLoading },
] = useLazyQuery(EMPLOYEE_BY_EMPLOYEE_ID, {
    fetchPolicy: "no-cache",
});

useEffect(() => {
    if (employeeID) {
        getEmployeeById({
            variables: {
                employee_id: Number(employeeID),
            },
        });
    }
}, [employeeID, getEmployeeById]);

    //api
    const [
        getLeavesByEmployee,
        { data, loading },
    ] = useLazyQuery(LEAVES_BY_EMPLOYEE, {
        fetchPolicy: 'no-cache'
    });

    useEffect(() => {
        if (employeeID) {
            const now = new Date();

            const startDate = format(startOfYear(now), "yyyy-MM-dd");
            const endDate = format(endOfYear(now), "yyyy-MM-dd");


            getLeavesByEmployee({
                variables: {
                    employee_id: employeeID,
                    start_date: `${startDate}T00:00:00`,
                    end_date: `${endDate}T23:59:59`
                }
            })
        }
    }, [employeeID])

    const toggleDate = (date: Date) => {
        const exists = selectedDates.some((d) => d.date.toDateString() === date.toDateString());

        if (exists) {
            setSelectedDates(selectedDates.filter((d) => d.date.toDateString() !== date.toDateString()));
        } else {
            setSelectedDates([
                ...selectedDates,
                { date, isHalfDay: false }  // default to full day
            ].sort((a, b) => a.date.getTime() - b.date.getTime()));
        }
    };

    const toggleHalfDay = (date: Date) => {
        setSelectedDates((prev) =>
            prev.map((d) =>
                d.date.toDateString() === date.toDateString()
                    ? { ...d, isHalfDay: !d.isHalfDay }
                    : d
            )
        );
    };

    const formattedData = useMemo(() => {
        if (data && data.leaves_by_employee.status_code === 200) {
            // setTotalPages(data?.defects?.pagination.total_pages)
            const leaves = data.leaves_by_employee.leaves || [];
            return leaves.map((l: any) => {
                return {
                    id: l.id,
                    start_date: format(new Date(l.start_date), "dd-MM-yyyy"),
                    end_date: format(new Date(l.end_date), "dd-MM-yyyy"),
                   is_half_day: l.is_half_day ? "Half Day" : "Full Day",
                    leave_type: l.leave_type,
                    reason: l.reason,
                    status: l.status,
                    pay_type: l.pay_type === "Partial" ? "Paid 0.5 + Unpaid 0.5" : l.pay_type,
                };
            });
        }
        else {
            return [];
        }
    }, [data]);

  const existingLeaves = data?.leaves_by_employee?.leaves || [];

// Approved/Pending requests ka total (sirf display ke liye, unpaid bhi isme aayegi)
const approvedPendingCount = useMemo(
    () =>
        existingLeaves
            .filter((l: any) => l.status === "Approved" || l.status === "Pending")
            .reduce((t: number, l: any) => t + (l.is_half_day ? 0.5 : 1), 0),
    [data]
);

// Balance: unpaid minus nahi hogi
const availableBalance = useMemo(() => {
    const employee =
        employeeData?.employee_by_employee_id?.employees?.[0];

    if (
        data?.leaves_by_employee?.status_code !== 200 ||
        employeeData?.employee_by_employee_id?.status_code !== 200 ||
        !employee?.date_of_joining
    ) {
        return 0;
    }

    return getAvailableBalance(
        existingLeaves,
        new Date(),
        employee.date_of_joining
    );
}, [data, employeeData]);
    return (
        <>
            <div id="cts-respon-p-dv" className="w-full  relative  overflow-hidden flex flex-row items-start justify-center flex-wrap content-start py-40 px-20 mq991:p-8 mq575:p-4  box-border text-left text-base text-gray font-manrope">
                <div className="w-full flex flex-col items-start justify-start gap-6">
                    <BreadCumb />
                    <div className="self-stretch flex flex-row items-center justify-start gap-[5px] text-2xl">
                        <img className="w-6 relative h-6 overflow-hidden shrink-0 cursor-pointer dark-white-img" alt="" src={backArrow}
                            onClick={() => navigate(-1)}
                        />
                        <h3 className="m-0 flex-1 relative text-inherit font-bold font-inherit inline-block max-w-[calc(100%_-_29px)] mq450:text-base dark:text-white">Apply Leave</h3>
                    </div>
                    <div className="m-0 self-stretch rounded-radius-radius-lg bg-surface-bg-blue darkborder-b-remove flex flex-col items-start justify-start p-8 mq575:p-3 box-border gap-spacing-lg max-w-full mq825:pt-5 mq825:pb-5 mq825:box-border mq1425:pt-[21px] mq1425:pb-[21px] mq1425:box-border">
                        <div className="min-w-full self-stretch flex flex-row  items-start justify-start py-0 pr-0.5 pl-0 box-border gap-[20px_18px] max-w-full text-base tablet-view-blg-dv">
                            <div className="min-w-full flex flex-col items-start justify-start gap-[20px]">
                                <div className="grid grid-cols-3 mq1900:grid-cols-3 mq1425:grid-cols-3 mq1275:grid-cols-2 mq950:grid-cols-1 mq825:grid-cols-1 mq575:grid-cols-1 w-full gap-[16px] ">
                                    <LeaveCalendar selectedDates={selectedDates} onDateClick={toggleDate} />
                                    <SelectedDates dates={selectedDates} onToggleHalfDay={toggleHalfDay} />
                                    <LeaveDetails selectedDates={selectedDates} />
                                </div>
                            </div>

                        </div>
                    </div>
                    <div className="m-0 self-stretch rounded-radius-radius-lg bg-surface-bg-blue darkborder-b-remove flex flex-col items-start justify-start p-8 mq575:p-3 box-border gap-spacing-lg max-w-full mq825:pt-5 mq825:pb-5 mq825:box-border mq1425:pt-[21px] mq1425:pb-[21px] mq1425:box-border">
                        <div className="flex w-full gap-[16px] counter_leave_section">
                            {data && (
                                <div className="grid grid-cols-3 mq1900:grid-cols-3 mq1425:grid-cols-3 mq1275:grid-cols-3 mq950:grid-cols-1 mq825:grid-cols-1 mq575:grid-cols-1 min-w-full gap-[16px]">
                                    <StatsPanel label="Total Days" value={selectedDates.length.toString()} icon="📅" />
                                    <StatsPanel label="Approved/Pending Requests" value={approvedPendingCount.toFixed(1)} icon="📄" handleShowLeaves={() => setShowLeaves(!showLeaves)} />
                                    <StatsPanel label="Available Balance" value={availableBalance.toFixed(1)} icon="⏳" />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                {showLeaves && data?.leaves_by_employee?.leaves?.length > 0 ? (
                    <div className="w-full">
                        <DynamicTable
                            filteredData={formattedData}
                            selectedColumns={columns}
                            sorting={sorting}
                            setSorting={setSorting}
                            selectedRows={selectedRows}
                            setSelectedRows={setSelectedRows}
                            setMainTable={setMainTable}
                            setMainTableUpdate={setMainTableUpdate}
                        />
                    </div>
                ) : (
                    <div className="w-full self-stretch rounded-lg  overflow-hidden rounded-lg flex flex-col items-center justify-center py-[20px] gap-[8px] text-center text-text-text-secondary mq450:pl-5 mq450:pr-5 mq450:box-border">
                        <div className="self-stretch bg-white rounded-lg bg-slate-100 overflow-hidden dark:bg-slate-700 flex flex-col items-center justify-center py-[110px] px-[53px] gap-[8px] text-center text-text-text-secondary mq450:pl-5 mq450:pr-5 mq450:box-border">
                            {/* <img
                            className="w-9 h-9 relative overflow-hidden shrink-0"
                            alt=""
                            // src={alertOrAction === "action" ? firsflag1 : iioniconaalertcircleoutline}
                        /> */}
                            <p className="relative my-0">No data available</p>
                        </div>
                    </div>
                )}
                {(loading || employeeLoading) && (
                    <Loader />
                )}
            </div >

        </>
    );
}

export default ApplyLeave;